'use client';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

// Initialize Google Analytics
export function initGA() {
  const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  
  if (!measurementId || typeof window === 'undefined') return;

  // Load gtag script if not already loaded
  if (!window.dataLayer) {
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.async = true;
    document.head.appendChild(script);

    window.dataLayer = [];
    window.gtag = function() {
      window.dataLayer?.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      page_path: window.location.pathname,
    });
  }
}

// Hook for automatic page view tracking
export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId || !window.gtag) return;

    const url = pathname + (searchParams?.toString() ? `?${searchParams}` : '');
    window.gtag('event', 'page_view', {
      page_path: url,
      page_title: document.title,
      page_location: window.location.href,
    });
  }, [pathname, searchParams]);
}

// Track custom events
export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

// Specific tracking functions for common events
export const analytics = {
  // Quote form tracking
  trackQuoteStart: () => {
    trackEvent('quote_form_start', {
      event_category: 'engagement',
      event_label: 'Quote Form Started',
    });
  },

  trackQuoteStep: (step: number, stepName: string) => {
    trackEvent('quote_form_step', {
      event_category: 'engagement',
      event_label: stepName,
      step_number: step,
    });
  },

  trackQuoteAbandonment: (step: number, stepName: string) => {
    trackEvent('quote_form_abandoned', {
      event_category: 'engagement',
      event_label: `Abandoned at ${stepName}`,
      step_number: step,
    });
  },

  trackQuoteSubmission: (services: string[], estimatedAmount: string) => {
    trackEvent('quote_submitted', {
      event_category: 'conversion',
      event_label: 'Quote Form Completed',
      services: services.join(', '),
      estimated_value: estimatedAmount,
      value: parseFloat(estimatedAmount.replace(/[^0-9.-]+/g, '')) || 0,
    });
  },

  // Service page tracking
  trackServiceView: (serviceName: string) => {
    trackEvent('service_view', {
      event_category: 'engagement',
      event_label: serviceName,
    });
  },

  trackServiceInterest: (serviceName: string, action: string) => {
    trackEvent('service_interest', {
      event_category: 'engagement',
      event_label: `${serviceName} - ${action}`,
      service: serviceName,
      action: action,
    });
  },

  // CTA tracking
  trackCTAClick: (ctaName: string, location: string) => {
    trackEvent('cta_click', {
      event_category: 'engagement',
      event_label: ctaName,
      location: location,
    });
  },

  // Navigation tracking
  trackNavigation: (destination: string, source: string) => {
    trackEvent('navigation', {
      event_category: 'engagement',
      event_label: `${source} to ${destination}`,
      destination: destination,
      source: source,
    });
  },

  // Form field tracking
  trackFormFieldInteraction: (fieldName: string, formName: string) => {
    trackEvent('form_field_interaction', {
      event_category: 'engagement',
      event_label: `${formName} - ${fieldName}`,
      form: formName,
      field: fieldName,
    });
  },

  // Booking tracking
  trackBookingStart: () => {
    trackEvent('booking_start', {
      event_category: 'conversion',
      event_label: 'Booking Process Started',
    });
  },

  trackBookingComplete: (amount: number) => {
    trackEvent('purchase', {
      event_category: 'conversion',
      transaction_id: Date.now().toString(),
      value: amount,
      currency: 'USD',
    });
  },
};